import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Check, Eraser, Mountain, Route, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function WaiverPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [health, setHealth] = useState("");
  const [agree, setAgree] = useState(false);
  const [typed, setTyped] = useState("");
  const [drawn, setDrawn] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [proxyForms, setProxyForms] = useState({});
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    document.title = "Hiking Waiver — Salt & Stone, Hydra";
    api.get(`/waiver/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(formatApiError(err)));
  }, [token]);

  useEffect(() => {
    if (canvasRef.current) {
      const c = canvasRef.current;
      c.width = c.offsetWidth;
      c.height = c.offsetHeight;
    }
  }, [data, result]);

  function canvasPos(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  }
  function startDraw(e) {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = canvasPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.preventDefault();
  }
  function draw(e) {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1C1917";
    const { x, y } = canvasPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setDrawn(true);
    e.preventDefault();
  }
  function endDraw() {
    drawing.current = false;
  }
  function clearCanvas() {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setDrawn(false);
  }

  async function submit(e) {
    e.preventDefault();
    if (!agree || (!drawn && !typed.trim())) return;
    const proxies = (data?.proxies || []).map((p) => ({
      name: p.name,
      relationship: proxyForms[p.name]?.relationship || "",
      health: (proxyForms[p.name]?.health || "").trim(),
    }));
    if (proxies.some((p) => !p.relationship || !p.health)) {
      toast.error("Please complete the waiver section for each person you're signing for");
      return;
    }
    setSending(true);
    try {
      const payload = {
        health: health.trim(),
        signature_text: typed.trim() || null,
        signature_image: drawn ? canvasRef.current.toDataURL("image/png") : null,
        proxies: proxies.length ? proxies : null,
      };
      const { data: res } = await api.post(`/waiver/${token}`, payload);
      setResult(res);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  const inputCls =
    "w-full rounded-xl border border-ink/20 bg-white px-4 py-3.5 text-lg text-ink outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-ink/35 focus:border-terra focus:ring-2 focus:ring-terra/20";

  const lastMinuteNote = (
    <p data-testid="waiver-last-minute" className="rounded-xl border border-terra/30 bg-terra/5 px-5 py-4 text-base leading-relaxed text-ink/75">
      <strong>For last minute bookings contact Vasilis directly:</strong>{" "}
      <a href="tel:+306976231058" className="text-terra underline underline-offset-4">+30 6976 231 058</a>
    </p>
  );

  let body;
  if (error) {
    body = (
      <div data-testid="waiver-error" className="rounded-3xl border border-red-200 bg-white p-9 text-center">
        <h1 className="font-serif text-3xl tracking-tight">This waiver link isn't valid</h1>
        <p className="mt-3 text-base text-ink/60">{error}</p>
        <Link to="/" className="mt-6 inline-block text-base uppercase tracking-[0.2em] text-terra underline underline-offset-8">
          Back to Salt &amp; Stone
        </Link>
      </div>
    );
  } else if (!data) {
    body = <div className="h-40" data-testid="waiver-loading" />;
  } else if (result || data.signed) {
    const allSigned = result ? result.all_signed : true;
    body = (
      <div data-testid="waiver-success" className="rounded-3xl border border-olive/30 bg-white p-9 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-olive text-paper">
          <Check size={26} />
        </span>
        <h1 className="mt-6 font-serif text-3xl tracking-tight sm:text-4xl">
          {allSigned ? "Everyone has signed — booking confirmed!" : "Waiver signed. Thank you!"}
        </h1>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-ink/70">
          {result
            ? allSigned
              ? `All participants for ${data.hike_name} on ${data.preferred_date} have now signed. The lead booker has received the confirmation email with payment details.`
              : `Your waiver for ${data.hike_name} on ${data.preferred_date} is saved. The booking is confirmed as soon as the remaining participants sign theirs.`
            : `Your waiver for ${data.hike_name} on ${data.preferred_date} was already signed${data.signed_at ? ` on ${new Date(data.signed_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : ""}.`}
        </p>
        <div className="mt-7">{lastMinuteNote}</div>
        <Link to="/" data-testid="waiver-home-link" className="mt-6 inline-block text-base uppercase tracking-[0.2em] text-terra underline underline-offset-8">
          Back to Salt &amp; Stone
        </Link>
      </div>
    );
  } else {
    body = (
      <form onSubmit={submit} data-testid="waiver-form" className="rounded-3xl border border-ink/15 bg-white p-7 sm:p-10">
        <div className="rounded-2xl bg-olive-pale/60 p-5" data-testid="waiver-booking-summary">
          <p className="text-sm uppercase tracking-[0.2em] text-ink/50">Booking details</p>
          <h2 className="mt-2 font-serif text-2xl tracking-tight">{data.hike_name}</h2>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-base text-ink/70">
            <span className="flex items-center gap-2"><CalendarDays size={15} className="text-terra" /> {data.preferred_date}</span>
            <span className="flex items-center gap-2"><Users size={15} className="text-terra" /> {data.people} {data.people === 1 ? "person" : "people"}</span>
            <span className="flex items-center gap-2"><Mountain size={15} className="text-terra" /> Lead: {data.lead_name}</span>
          </div>
        </div>

        <div className="mt-7 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Participant full name</span>
            <input value={data.name} readOnly data-testid="waiver-name" className={`${inputCls} bg-paper text-ink/60`} />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Email</span>
            <input value={data.email} readOnly data-testid="waiver-email" className={`${inputCls} bg-paper text-ink/60`} />
          </label>
        </div>

        <label className="mt-6 block">
          <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Health disclosure (required)</span>
          <textarea rows={4} required value={health} onChange={(e) => setHealth(e.target.value)}
            placeholder="Medical conditions, allergies, physical limitations — or write 'None'"
            data-testid="waiver-health" className={inputCls} />
        </label>

        {(data.proxies || []).length > 0 && (
          <div className="mt-6 space-y-5" data-testid="waiver-proxies">
            {data.proxies.map((p) => (
              <div key={p.name} className="rounded-2xl border border-terra/25 bg-terra/5 p-5" data-testid={`proxy-section-${p.name.replace(/\s+/g, "-")}`}>
                <p className="font-serif text-xl tracking-tight">Waiver for {p.name}</p>
                <p className="mt-1 text-sm text-ink/60">You are signing on behalf of this person.</p>
                <label className="mt-4 block">
                  <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Your relationship</span>
                  <select
                    required
                    value={proxyForms[p.name]?.relationship || ""}
                    onChange={(e) => setProxyForms({ ...proxyForms, [p.name]: { ...proxyForms[p.name], relationship: e.target.value } })}
                    data-testid={`proxy-relationship-${p.name.replace(/\s+/g, "-")}`}
                    className={inputCls}
                  >
                    <option value="" disabled>Choose…</option>
                    <option value="parent">Parent</option>
                    <option value="guardian">Guardian</option>
                    <option value="representative">Authorized representative</option>
                  </select>
                </label>
                <label className="mt-4 block">
                  <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">
                    Health disclosure for {p.name} (required)
                  </span>
                  <textarea
                    rows={3}
                    required
                    value={proxyForms[p.name]?.health || ""}
                    onChange={(e) => setProxyForms({ ...proxyForms, [p.name]: { ...proxyForms[p.name], health: e.target.value } })}
                    placeholder="Medical conditions, allergies, physical limitations — or 'None'"
                    data-testid={`proxy-health-${p.name.replace(/\s+/g, "-")}`}
                    className={inputCls}
                  />
                </label>
                <p className="mt-3 text-sm text-ink/55">
                  The liability release below and your signature cover {p.name} as well.
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-ink/15 bg-paper p-5" data-testid="waiver-liability">
          <p className="text-sm uppercase tracking-[0.2em] text-ink/50">Liability release</p>
          <p className="mt-3 text-base leading-relaxed text-ink/75">
            I understand that hiking involves inherent risks — uneven terrain, heat, weather changes, slips and falls.
            I confirm that I am physically able to take part, that I will follow the guide's instructions at all times,
            and I agree that Vasilis Kastritseas is not liable for accidents, injuries or loss occurring during the
            activity, to the fullest extent permitted by law.
          </p>
          <label className="mt-4 flex cursor-pointer items-start gap-3 text-base text-ink/80">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
              data-testid="waiver-agree" className="mt-1 h-5 w-5 shrink-0 accent-terra" />
            I have read and agree to the liability release above.
          </label>
        </div>

        <div className="mt-6">
          <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Signature — draw below</span>
          <div className="relative overflow-hidden rounded-xl border border-ink/25 bg-white">
            <canvas
              ref={canvasRef}
              data-testid="waiver-canvas"
              className="h-40 w-full touch-none cursor-crosshair"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
            <button type="button" onClick={clearCanvas} data-testid="waiver-clear-signature"
              className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full border border-ink/15 bg-white/90 px-3 py-1.5 text-sm text-ink/60 transition-colors duration-300 hover:border-terra hover:text-terra">
              <Eraser size={13} /> Clear
            </button>
          </div>
          <p className="mb-2 mt-5 block text-sm uppercase tracking-[0.2em] text-ink/60">…or type your full name</p>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={data.name}
            data-testid="waiver-typed-signature" className={`${inputCls} font-serif italic`} />
        </div>

        <label className="mt-6 block">
          <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Date</span>
          <input value={today} readOnly data-testid="waiver-date" className={`${inputCls} bg-paper text-ink/60`} />
        </label>

        <button type="submit" disabled={sending || !agree || (!drawn && !typed.trim())}
          data-testid="waiver-submit"
          className="mt-8 w-full rounded-full bg-terra py-4 text-base uppercase tracking-[0.18em] text-paper transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-terra-dark disabled:opacity-50">
          {sending ? "Signing…" : "Sign waiver"}
        </button>
        <p className="mt-4 text-center text-sm leading-relaxed text-ink/45">
          Signed digitally as {data.name} · {today}
        </p>
      </form>
    );
  }

  return (
    <main data-testid="waiver-page" className="min-h-screen bg-paper pt-28 sm:pt-32">
      <div className="mx-auto max-w-2xl px-5 pb-24 sm:px-8">
        <p className="overline-label text-terra">Salt &amp; Stone · Hydra</p>
        <h1 className="display-serif mt-3 text-4xl leading-none sm:text-5xl">Hiking waiver</h1>
        <p className="mt-4 text-base leading-relaxed text-ink/65">
          A quick health disclosure and liability release — required from every participant before the booking is confirmed.
        </p>
        <div className="mt-8">{body}</div>
        {!result && data && !data.signed && !error && <div className="mt-6">{lastMinuteNote}</div>}
      </div>
    </main>
  );
}
